/** Channel-handling helpers that both the API and the widget rely on. */

/**
 * Twilio delivers `+15551234567` while the widget's phone field is typed by a
 * human, so the same customer could be stored either way. The original queried
 * both spellings with `phone = X OR phone = <X without +1>`; this reproduces
 * that as an explicit candidate list.
 */
export function phoneVariants(phone: string): string[] {
  const trimmed = phone.trim();
  if (!trimmed) return [];

  const digits = trimmed.replace(/\D/g, '');
  const candidates = new Set<string>([trimmed]);

  if (digits) {
    candidates.add(digits);
    candidates.add(`+${digits}`);

    // US numbers: with and without the country code.
    if (digits.length === 11 && digits.startsWith('1')) {
      const local = digits.slice(1);
      candidates.add(local);
      candidates.add(`+1${local}`);
    } else if (digits.length === 10) {
      candidates.add(`1${digits}`);
      candidates.add(`+1${digits}`);
    }
  }

  return [...candidates];
}

/** E.164 for outbound Twilio sends; assumes US when no country code is present. */
export function toE164(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (!digits) return '';
  if (phone.trim().startsWith('+')) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  return `+${digits}`;
}

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}

/**
 * Strip the quoted tail from an email reply so the conversation shows only what
 * the customer actually typed. Mirrors the original cut points, plus the two
 * `On <date> ... wrote:` forms Gmail and Outlook emit.
 */
export function stripQuotedReply(text: string): string {
  const lines = text.split(/\r?\n/);
  const body: string[] = [];

  for (const line of lines) {
    const lower = line.toLowerCase();

    if (lower.includes('-original message-')) break;
    if (lower.includes('via leadgab wrote')) break;
    if (lower.includes('about craigslist mail')) break;
    if (/^-{5,}\s*$/.test(line.trim())) break;
    if (/^on .+ wrote:\s*$/i.test(line.trim())) break;
    if (/^_{5,}\s*$/.test(line.trim())) break;

    if (line.startsWith('>')) continue;

    body.push(line);
  }

  return body.join('\n').trim();
}

/** Deterministic placeholder avatar; the original called the DiceBear HTTP API. */
export function avatarUrl(style: 'initials' | 'shapes' | 'identicon' | 'bottts', seed: string): string {
  return `https://api.dicebear.com/9.x/${style}/svg?seed=${encodeURIComponent(seed)}`;
}

/** Fill `{{placeholders}}` in a message template. Unknown keys collapse to ''. */
export function renderTemplate(template: string, values: Record<string, string | null | undefined>): string {
  return template.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_match, key: string) => values[key] ?? '');
}

/**
 * Image messages are stored as a comma-joined URL list (the original packed
 * them into the same TEXT column as prose), so splitting has to tolerate the
 * stray commas that customer text introduces.
 */
export function splitImageList(content: string): string[] {
  return content
    .split(',')
    .map((part) => part.trim())
    .filter((part) => /^https?:\/\//.test(part));
}

export function truncate(value: string, max: number): string {
  if (value.length <= max) return value;
  return `${value.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
}

function fieldName(entry: unknown): string | null {
  const name = (entry as { name?: unknown } | null)?.name;
  return typeof name === 'string' && name.length > 0 ? name : null;
}

/**
 * The input names a tracked form was configured against.
 *
 * Three shapes are in the database and all three are live: the agent app stores
 * a flat list of names, the seed and the earlier API stored a flat list of
 * `{name}`, and WordPress stored the scanned DOM one entry per form on the page,
 * with the form's own `index` picking which entry described it.
 *
 * Shared because the capture worker matches submissions against this list and
 * the form editor offers it as the choices for "which field holds what" — two
 * readings of the same column that must not drift apart. (The widget carries its
 * own copy: it is a standalone bundle with no workspace dependencies, and giving
 * it one to save nine lines would put the whole of this package on a customer's
 * page.)
 */
export function trackedFormFieldNames(source: unknown, index = 0): string[] {
  if (!Array.isArray(source)) return [];

  if (source.every((entry) => typeof entry === 'string')) return source as string[];

  const flat = source.map(fieldName).filter((name): name is string => name !== null);
  if (flat.length > 0) return flat;

  const entry =
    (source[index] as { children?: unknown } | undefined) ??
    // An index that no longer addresses anything — the page changed shape since
    // the form was saved — should still match on names rather than give up.
    (source.find((candidate) => Array.isArray((candidate as { children?: unknown })?.children)) as
      | { children?: unknown }
      | undefined);

  if (!entry || !Array.isArray(entry.children)) return [];

  return entry.children.map(fieldName).filter((name): name is string => name !== null);
}

/** The mappings the form editor manages. Everything else on a form is passed through. */
export const TRACKED_FORM_LABELS = ['Name', 'Email', 'Phone'] as const;

export type TrackedFormLabel = (typeof TRACKED_FORM_LABELS)[number];

/**
 * The four roles a `mapFields` key can name, whatever it is spelled as.
 *
 * `Name`, `customerName` and `name` are all in the database, written by the
 * agent app, the seed and WordPress respectively. Case and a `customer` prefix
 * carry no meaning, so both come off before anything is compared.
 */
export function trackedFormRole(key: string): string {
  return key.toLowerCase().replace(/[^a-z]/g, '').replace(/^customer/, '');
}

/** Reads a saved mapping into the editor's selects. */
export function readTrackedFormMapping(
  mapFields: Record<string, unknown>,
): Record<TrackedFormLabel, string> {
  const mapping: Record<TrackedFormLabel, string> = { Name: '', Email: '', Phone: '' };

  for (const [key, field] of Object.entries(mapFields)) {
    const label = TRACKED_FORM_LABELS.find(
      (candidate) => candidate.toLowerCase() === trackedFormRole(key),
    );

    if (label && typeof field === 'string') mapping[label] = field;
  }

  return mapping;
}

/**
 * Writes the editor's selects back, keeping every other key the row already had.
 *
 * This is the whole reason it exists. A form imported from WordPress carries
 * `message`, `markup` and `required` in this column, which drive the composed
 * message and the rules for refusing an incomplete submission. Rebuilding the
 * object from the three selects alone would delete all three the first time
 * somebody renamed the form.
 */
export function writeTrackedFormMapping(
  existing: Record<string, unknown>,
  mapping: Record<TrackedFormLabel, string>,
): Record<string, unknown> {
  const next: Record<string, unknown> = {};

  for (const [key, field] of Object.entries(existing)) {
    if (TRACKED_FORM_LABELS.some((label) => label.toLowerCase() === trackedFormRole(key))) continue;
    next[key] = field;
  }

  for (const label of TRACKED_FORM_LABELS) {
    // An empty select means "this form has no such field", not "map it to ''".
    if (mapping[label]) next[label] = mapping[label];
  }

  return next;
}
